import React, { useState, useRef } from 'react';
import { FormGroup, Label, Input, Col, Button, Alert } from 'reactstrap';
import ImageUploading from 'react-images-uploading';
import { ModalWindow } from '../../modal/ModalWindow';
import { ApiEndpoints } from '../../../api/apiEndpoints';
import { RequiredSvgIcon } from '../../../assets/shop/RequiredSvgIcon';

export function IllustrationCreateEdit({ isEditing, setShowModal, asset, fetchAssets, handleDelete, assetType }) {
    const modalRef = useRef(null);
    const [description, setDescription] = useState(asset.description || '');
    const [thumbnail, setThumbnail] = useState(null);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [original, setOriginal] = useState(null);
    const [originalUrl, setOriginalUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleThumbnailChange = (imageList) => {
        if (imageList.length > 0) {
            setThumbnail(imageList[0].file);
            setThumbnailUrl(imageList[0].data_url);
        } else {
            setThumbnail(null);
            setThumbnailUrl(null);
        }
    };

    const handleOriginalChange = (imageList) => {
        if (imageList.length > 0) {
            setOriginal(imageList[0].file);
            setOriginalUrl(imageList[0].data_url);
        } else {
            setOriginal(null);
            setOriginalUrl(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!isEditing && !original) {
            setError('Original image is required.');
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('description', description);
            formData.append('sorting', asset.sorting || 0);

            if (thumbnail) formData.append('thumbnail', thumbnail);
            if (original) formData.append('original', original);

            let url, method;
            if (isEditing) {
                url = `${ApiEndpoints.ManageIllustrations}/${asset.id}`;
                method = 'PUT';
            } else {
                url = `${ApiEndpoints.ManageIllustrations}?type=${assetType}`;
                method = 'POST';
            }

            const response = await fetch(url, { method, body: formData });
            if (!response.ok) throw new Error('Failed to save illustration');

            fetchAssets();
            modalRef.current.closeWindow();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = () => {
        handleDelete(asset.id);
        modalRef.current.closeWindow();
    };

    return (
        <ModalWindow
            ref={modalRef}
            title={isEditing ? 'Edit Illustration' : 'Add Illustration'}
            onClose={() => setShowModal(false)}
        >
            <form onSubmit={handleSubmit}>
                {error && <Alert color="danger">{error}</Alert>}

                <FormGroup row>
                    <Label for="description" sm={2}>Description</Label>
                    <Col sm={10}>
                        <Input
                            type="text"
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Col>
                </FormGroup>

                {!isEditing && (
                    <>
                        <FormGroup row>
                            <Label className="m-auto" sm={2}>Thumbnail</Label>
                            <Col sm={10}>
                                <ImageUploading
                                    value={thumbnail ? [{ data_url: thumbnailUrl }] : []}
                                    onChange={handleThumbnailChange}
                                    dataURLKey="data_url"
                                    acceptType={["jpg", "jpeg", "png"]}
                                >
                                    {({ imageList, onImageUpload, onImageRemove }) => (
                                        <div className="upload__image-wrapper d-flex">
                                            <Button className="upload-image-btn" color="primary" onClick={onImageUpload}>
                                                Upload Thumbnail
                                            </Button>
                                            &nbsp;
                                            {imageList.length > 0 && (
                                                <div>
                                                    <img className="upload-image-preview" src={imageList[0].data_url} alt="" width="100" />
                                                    <Button color="danger" onClick={() => onImageRemove(0)}>Remove</Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </ImageUploading>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label className="m-auto" sm={2}>
                                Original
                                <div className="required d-inline"><RequiredSvgIcon /></div>
                            </Label>
                            <Col sm={10}>
                                <ImageUploading
                                    value={original ? [{ data_url: originalUrl }] : []}
                                    onChange={handleOriginalChange}
                                    dataURLKey="data_url"
                                    acceptType={["jpg", "jpeg", "png"]}
                                >
                                    {({ imageList, onImageUpload, onImageRemove }) => (
                                        <div className="upload__image-wrapper d-flex">
                                            <Button color="primary" className="upload-image-btn" onClick={onImageUpload}>
                                                Upload Original
                                            </Button>
                                            &nbsp;
                                            {imageList.length > 0 && (
                                                <div>
                                                    <img className="upload-image-preview" src={imageList[0].data_url} alt="" width="100" />
                                                    <Button color="danger" onClick={() => onImageRemove(0)}>Remove</Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </ImageUploading>
                            </Col>
                        </FormGroup>
                    </>
                )}

                {isEditing && (
                    <FormGroup row>
                        <Label className="m-auto" sm={2}>Preview</Label>
                        <Col sm={10}>
                            <img
                                src={ApiEndpoints.PublicFile + "/" + asset.thumbnailFileName}
                                alt={asset.description}
                                height="150"
                            />
                        </Col>
                    </FormGroup>
                )}

                <div className="d-flex justify-content-between mt-3">
                    <Button color="success" className="save-btn" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    {isEditing && (
                        <Button color="danger" className="remove-asset-btn" onClick={onDelete}>
                            Delete
                        </Button>
                    )}
                </div>
            </form>
        </ModalWindow>
    );
}
